import React, { createContext, useContext, useEffect, useState } from 'react'
import { User, Session, AuthChangeEvent } from '@supabase/supabase-js'
import { supabase } from './supabase'
import { getUserProfile } from './api/api'

interface AuthContextType {
  isAuthenticated: boolean
  user: User | null
  session: Session | null
  isBanned: boolean
  bannedUntil: string | null
  isTOTPVerified: boolean
  setIsTOTPVerified: (verified: boolean) => void
  login: (email: string, password: string) => Promise<void>
  signup: (email: string, password: string) => Promise<{ session: Session | null, user: User | null }>
  logout: () => Promise<void>
  refreshSession: () => Promise<void>
  getToken: () => string | null
  checkBanStatus: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [isBanned, setIsBanned] = useState(false)
  const [bannedUntil, setBannedUntil] = useState<string | null>(null)
  const [isTOTPVerified, setIsTOTPVerified] = useState(false)

  useEffect(() => {
    // Check active sessions and sets the user
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsAuthenticated(!!session)
      setUser(session?.user ?? null)
      setSession(session)
      // Reset TOTP verification on new session
      setIsTOTPVerified(false)
    })

    // Listen for changes on auth state (logged in, signed out, etc.)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event: AuthChangeEvent, session: Session | null) => {
        console.log('[AuthContext] Auth state change:', event)
        setIsAuthenticated(!!session)
        setUser(session?.user ?? null)
        setSession(session)
        // Always reset TOTP verification on any auth state change except manual verification
        if (event === 'SIGNED_OUT' || event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          console.log('[AuthContext] Resetting TOTP verification due to auth state change:', event)
          setIsTOTPVerified(false)
        }
      }
    )

    return () => subscription.unsubscribe()
  }, [])

  const refreshSession = async () => {
    const { data: { session }, error } = await supabase.auth.refreshSession()
    if (error) throw error
    setSession(session)
    setUser(session?.user ?? null)
    setIsAuthenticated(!!session)
  }

  const getToken = (): string | null => {
    return session?.access_token ?? null
  }

  const login = async (email: string, password: string) => {
    console.log('[AuthContext] Starting login process')
    // Always reset TOTP verification on new login attempts
    setIsTOTPVerified(false)

    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    if (error) throw error

    console.log('[AuthContext] Password authentication successful, TOTP verification required')
  }

  const signup = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    })
    if (error) throw error
    return { session: data.session, user: data.user }
  }

  const logout = async () => {
    try {
      const { error } = await supabase.auth.signOut()
      if (error) {
        console.warn('Error during signOut:', error.message)
      }
      // Always clear local state regardless of API errors
      setIsAuthenticated(false)
      setUser(null)
      setSession(null)
      setIsTOTPVerified(false)
    } catch (error) {
      console.error('Exception during logout:', error)
      // Still clear local state even if there's an exception
      setIsAuthenticated(false)
      setUser(null)
      setSession(null)
      setIsTOTPVerified(false)
    }
  }

  const checkBanStatus = async () => {
    if (!user) return
    try {
      const userProfile = await getUserProfile()
      const isBannedNow = !!userProfile.bannedUntil && new Date(userProfile.bannedUntil) > new Date()
      setIsBanned(isBannedNow)
      setBannedUntil(userProfile.bannedUntil)

      if (isBannedNow) {
        await logout()
      }
    } catch (error) {
      console.error('Error checking ban status:', error)
    }
  }

  useEffect(() => {
    if (user) {
      checkBanStatus()
    }
  }, [user])

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      user,
      session,
      isBanned,
      bannedUntil,
      isTOTPVerified,
      setIsTOTPVerified,
      login,
      signup,
      logout,
      refreshSession,
      getToken,
      checkBanStatus
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
